<!-- eslint-disable max-len -->
<template>
  <div
    class="spa-guest-details-form"
  >
    <button
      :id="'guest-button-' + index"
      type="button"
      class="spa-guest-details-form__toggle"
      :class="{'--opened': isOpen}"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="'guest-content-' + index"
      @click.prevent.prevent="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <span class="spa-guest-details-form__title">{{ tc('spaSoft.step1.guestTimeDateSelection.guestFieldLabel', {guestNumber: Number(index) + 1}) }}</span>
      <span
        class="spa-guest-details-form__icon"
        :class="isOpen ? 'icon-minus' : 'icon-add'"
      />
    </button>
    <transition name="slide-fade">
      <section
        v-show="isOpen"
        :id="'guest-content-' + index"
        class="spa-guest-details-form__content"
        :aria-labelledby="'guest-button-' + index"
      >
        <fieldset>
          <legend class="sr-only">
            {{ tc('spaSoft.step1.guestTimeDateSelection.guestFieldLabel', {guestNumber: Number(index) + 1}) }}
          </legend>
          <div class="flex-grid-2-col gutter-md">
            <div class="input">
              <input
                :id="`firstName-${index}`"
                v-model="formData.firstName"
                :tabindex="isOpen ? '0' : '-1'"
                type="text"
                class="req"
                :name="`firstName-${index}`"
                :autocomplete="`guest-${index}-first-name`"
                maxlength="255"
                data-val="true"
                required
                data-rule-minlength="1"
                data-rule-maxlength="50"
                :data-msg-minlength="getValidationForField('minChars', '1')"
                :data-msg-maxlength="getValidationForField('maxChars', '50')"
                :data-val-required="getValidationForField('required', 'firstName')"
                :data-val-no-special-chars="tc('validations.noSpecialChars')"
                data-field-type="SingleLineText"
              >
              <label
                :for="`firstName-${index}`"
                class="req floating"
              >
                {{ tc('spaSoft.step2.form.firstName') }}
              </label>
              <span
                :data-valmsg-for="`firstName-${index}`"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
            <div class="input">
              <input
                :id="`lastName-${index}`"
                v-model="formData.lastName"
                :tabindex="isOpen ? '0' : '-1'"
                type="text"
                class="req"
                :name="`lastName-${index}`"
                :autocomplete="`guest-${index}-last-name`"
                maxlength="255"
                data-val="true"
                required
                data-rule-minlength="1"
                data-rule-maxlength="65"
                :data-msg-minlength="getValidationForField('minChars', '1')"
                :data-msg-maxlength="getValidationForField('maxChars', '65')"
                :data-val-required="getValidationForField('required', 'lastName')"
                :data-val-no-special-chars="tc('validations.noSpecialChars')"
                data-field-type="SingleLineText"
              >
              <label
                :for="`lastName-${index}`"
                class="req floating"
              >
                {{ tc('spaSoft.step2.form.lastName') }}
              </label>
              <span
                :data-valmsg-for="`lastName-${index}`"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
            <div class="input dropdown">
              <select
                :id="`gender-${index}`"
                v-model="formData.gender"
                :name="`gender-${index}`"
                class=""
                data-val="true"
                required
                :data-val-required="getValidationForField('required', 'guestGenderLabel')"
                data-field-type="DropdownList"
              >
                <option value="" />
                <option
                  v-for="gender in genders"
                  :key="gender.value"
                  :value="gender.value"
                >
                  {{ gender.label }}
                </option>
              </select>
              <label
                :for="`gender-${index}`"
                class="req floating"
              >
                {{ tc('spaSoft.step2.form.guestGenderLabel') }}
              </label>
              <span
                :data-valmsg-for="`gender-${index}`"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
            <div class="input dropdown">
              <select
                :id="`guestType-${index}`"
                v-model="formData.guestType"
                :name="`guestType-${index}`"
                class=""
                data-val="true"
                required
                :data-val-required="getValidationForField('required', 'guestType')"
                data-field-type="DropdownList"
              >
                <option value="" />
                <option
                  v-for="guestType in guestTypes"
                  :key="guestType.value"
                  :value="guestType.value"
                >
                  {{ guestType.label }}
                </option>
              </select>
              <label
                :for="`guestType-${index}`"
                class="req floating"
              >
                {{ tc('spaSoft.step2.form.guestType') }}
              </label>
              <span
                :data-valmsg-for="`guestType-${index}`"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
            <div class="input">
              <input
                :id="`email-${index}`"
                v-model="formData.email"
                type="text"
                class="req"
                :name="`email-${index}`"
                :autocomplete="`guest-${index}-email`"
                maxlength="255"
                data-val="true"
                required
                data-val-external="email"
                data-rule-minlength="6"
                data-rule-maxlength="255"
                :data-msg-minlength="getValidationForField('minChars', '6')"
                :data-msg-maxlength="getValidationForField('maxChars', '255')"
                :data-external-msg="getValidationForField('externalEmail')"
                :data-val-required="getValidationForField('required', 'emailAddress')"
                :data-val-regex="getValidationForField('email')"
                data-val-regex-pattern="^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,17}$"
                data-field-type="Email"
              >
              <label
                :for="`email-${index}`"
                class="req floating"
              >
                {{ tc('spaSoft.step2.form.emailAddress') }}
              </label>
              <span
                :data-valmsg-for="`email-${index}`"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
            <div class="input">
              <input
                :id="`emailConfirmation-${index}`"
                v-model="formData.emailConfirmation"
                type="text"
                class="req"
                :name="`emailConfirmation-${index}`"
                :autocomplete="`guest-${index}-email-confirmation`"
                maxlength="255"
                data-val="true"
                required
                data-val-external="email"
                data-rule-minlength="6"
                data-rule-maxlength="255"
                :data-msg-minlength="getValidationForField('minChars', '6')"
                :data-msg-maxlength="getValidationForField('maxChars', '255')"
                :data-external-msg="getValidationForField('externalEmail')"
                :data-val-regex="getValidationForField('email')"
                :data-val-equals-to="`#email-${index}`"
                :data-val-equals="getValidationForField('confirmationField', 'emailAddress')"
                data-val-regex-pattern="^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,17}$"
                data-field-type="Email"
              >
              <label
                :for="`emailConfirmation-${index}`"
                class="req floating"
              >
                {{ tc('spaSoft.step2.form.confirmEmailAddress') }}
              </label>
              <span
                :data-valmsg-for="`emailConfirmation-${index}`"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
          </div>
          <div class="flex-grid-3-col gutter-md">
            <div class="input dropdown phone-type">
              <select
                :id="`phoneType-${index}`"
                v-model="formData.phoneType"
                :name="`phoneType-${index}`"
                class=""
                data-val="true"
                required
                :data-val-required="getValidationForField('required', 'phoneType')"
                data-field-type="DropdownList"
              >
                <option value="" />
                <option
                  v-for="phoneType in phoneTypes"
                  :key="phoneType.value"
                  :value="phoneType.value"
                >
                  {{ phoneType.label }}
                </option>
              </select>
              <label
                :for="`phoneType-${index}`"
                class="req floating"
              >
                {{ tc('spaSoft.step2.form.phoneType') }}
              </label>
              <span
                :data-valmsg-for="`phoneType-${index}`"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
            <div class="input dropdown country-code">
              <phone-country-select>
                <template #default="{ handleSelectChange }">
                  <select
                    :id="`phoneCountryCode-${index}`"
                    v-model="formData.phoneCountryCode"
                    :name="`phoneCountryCode-${index}`"
                    data-val="true"
                    data-val-external="phone"
                    :data-external-msg="getValidationForField('externalPhone')"
                    :data-val-required="getValidationForField('required', 'phoneCountryCode')"
                    data-field-type="DropdownList"
                    :autocomplete="`guest-${index}-tel-country-code`"
                    @change="handleSelectChange"
                  >
                    <option value="" />
                    <option
                      v-for="country in countries"
                      :key="country.Name + 'Phone'"
                      :value="country.CallingCode"
                      :data-code="`+${country.CallingCode}`"
                      :data-country="country.Name"
                    >
                      {{ country.Name }} (+{{ country.CallingCode }})
                    </option>
                  </select>
                </template>
              </phone-country-select>
              <label
                :for="`phoneCountryCode-${index}`"
                class="req floating"
              >
                {{ tc('spaSoft.step2.form.phoneCountryCode') }}
              </label>
              <span
                :data-valmsg-for="`phoneCountryCode-${index}`"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
            <div class="input phone">
              <input
                :id="`phoneNumber-${index}`"
                v-model="formData.phoneNumber"
                type="tel"
                :name="`phoneNumber-${index}`"
                :autocomplete="`guest-${index}-tel`"
                data-val="true"
                required
                data-val-external="phone"
                :data-external-msg="getValidationForField('externalPhone')"
                :data-val-required="getValidationForField('required', 'phoneNumber')"
                data-val-regex-pattern="^[\d\+\-\(\)\s]*$"
                :data-val-regex="tc('validations.phone')"
                data-rule-minlength="5"
                data-rule-maxlength="17"
                :data-msg-minlength="getValidationForField('minChars', '5')"
                :data-msg-maxlength="getValidationForField('maxChars', '17')"
                :data-val-phone-number="tc('validations.phone')"
                data-field-type="PhoneNumber"
              >
              <label
                :for="`phoneNumber-${index}`"
                class="req floating"
              >
                {{ tc('spaSoft.step2.form.phoneNumber') }}
              </label>
              <span
                :data-valmsg-for="`phoneNumber-${index}`"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
          </div>
          <div class="flex-grid-1-col gutter-md">
            <div class="input textarea">
              <label
                class=""
                :for="`comments-${index}`"
              >
                {{ tc('spaSoft.step2.form.comentsLabel') }}
              </label>
              <textarea
                :id="`comments-${index}`"
                v-model="formData.comments"
                :name="`comments-${index}`"
                rows="10"
                :placeholder="tc('spaSoft.step2.form.comentsPlaceholder')"
              />
            </div>
          </div>
          <input
            :id="`iruProfileId-${index}`"
            v-model="formData.iruProfileId"
            :name="`iruProfileId-${index}`"
            type="hidden"
          >
        </fieldset>
      </section>
    </transition>
  </div>
</template>

<script>
  import { mapGetters, mapState, mapMutations } from 'vuex'
  import PhoneCountrySelect from '@components/01.base/01.forms/select/phone-country-select'
  import { SPA_SOFT_PHONE_TYPES, SPA_SOFT_GENDERS } from '@utils/constants'

  export default {
    name: 'SpaGuestDetailsForm',
    components: {
      PhoneCountrySelect
    },
    inject: ['phoneTypes', 'genders', 'guestTypes'],
    props: {
      index: {
        type: String,
        required: true
      }
    },
    data() {
      return {
        isOpen: true,
        formData: {
          firstName: '',
          lastName: '',
          gender: '',
          guestType: '',
          email: '',
          emailConfirmation: '',
          phoneType: '',
          phoneCountryCode: '',
          phoneNumber: '',
          comments: '',
          iruProfileId: ''
        }
      }
    },
    computed: {
      ...mapState('global', ['countries']),
      ...mapState('auth', { authUser: 'user' }),
      ...mapState('spaSoft', ['selectedItinerary']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('spaSoft', ['guestsSelectedData']),
      isSingleGuest() {
        return Object.keys(this.guestsSelectedData).length === 1
      },
    },
    watch: {
      formData: {
        handler() {
          this.checkIfRequiredFieldsAreFilled()
        },
        deep: true
      }
    },
    created() {
      if (this.isSingleGuest && this.authUser) {
        this.formData.firstName = this.authUser?.firstName ?? ''
        this.formData.lastName = this.authUser?.lastName ?? ''
        this.formData.gender = SPA_SOFT_GENDERS[this.authUser?.gender] ?? ''
        this.formData.email = this.authUser?.email ?? ''
        this.formData.emailConfirmation = this.authUser?.email ?? ''
        this.formData.phoneType = SPA_SOFT_PHONE_TYPES[this.authUser?.phoneNumbers?.[0]?.type] ?? ''
        this.formData.phoneCountryCode = this.authUser?.phoneNumbers?.[0]?.countryCode?.replace('+', '') ?? ''
        this.formData.phoneNumber = this.authUser?.phoneNumbers?.[0]?.number ?? ''
        this.formData.iruProfileId = this.authUser?.personId ?? ''

        this.$nextTick(() => {
          this.checkIfRequiredFieldsAreFilled()
        })
      }
    },
    mounted() {
      this.$nextTick(() => {
        this.checkIfRequiredFieldsAreFilled()
      })
    },
    methods: {
      ...mapMutations('spaSoft', ['UPDATE_GUEST_DATA']),

      updateGuestData(field, value) {
        const payload = {
          index: parseInt(this.index, 10),
          field,
          value: typeof value === 'string' ? value.trim() : value
        }
        this.UPDATE_GUEST_DATA(payload)
      },

      toggle() {
        this.isOpen = !this.isOpen
      },
      getValidationForField(validation, field = '') {
        return this.tc(`validations.${validation}`).replace('{0}', this.tc(`spaSoft.step2.form.${field}`))
      },
      getDefaultFieldValue(field = '') {
        if (!this.authUser || !this.isSingleGuest) return ''

        const keys = field.split('.')
        return keys.reduce((obj, key) => obj?.[key] ?? '', this.authUser)
      },
      checkIfRequiredFieldsAreFilled() {
        setTimeout(() => {
          const requiredField = [
            'firstName',
            'lastName',
            'gender',
            'guestType',
            'email',
            'emailConfirmation',
            'phoneType',
            'phoneCountryCode',
            'phoneNumber',
          ]

          const allFieldsFilledWithoutError = requiredField.every((field) => {
            const isFilled = typeof this.formData[field] === 'string' ? this.formData[field].trim() !== '' : this.formData[field] !== ''
            const hasErrorClass = this.$el.querySelector(`#${field}-${this.index}`)?.classList.contains('input-validation-error')
            const externalMsg = this.$el.querySelector(`#${field}-${this.index}`)?.getAttribute('data-external-msg')
            const currentError = this.$el.querySelector(`#${field}-${this.index}`)?.dataset.msg
            const isWarningOnly = currentError ? currentError === externalMsg : !!externalMsg
            return isFilled && (!hasErrorClass || isWarningOnly)
          })

          Object.entries(this.formData).forEach(([field, value]) => {
            this.updateGuestData(field, value)
          })

          this.$emit('guest-validation-changed', {
            index: this.index,
            status: allFieldsFilledWithoutError
          })
        }, 300)
      }
    },
  }
</script>
